import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Download, HandCoins, RotateCcw, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { parseCreditAmount } from '@kk-pos/shared';
import { api, saveBlob } from '@/lib/api/client';
import type {
  AccountCreditGiftInfo,
  AccountCreditOperation,
  AccountCreditRequest,
  AccountCreditResult,
  CreditAccount,
  CreditRecipientInput,
} from '@/lib/api/types';
import { useAtLeast } from '@/lib/auth';
import { dateTime, money, num, time } from '@/lib/format';
import { asOptionalText } from '@/lib/searchParams';
import { AccountPicker } from '@/features/account-credits/AccountPicker';
import {
  describeCommitFailure,
  describePreviewFailure,
} from '@/features/account-credits/conflicts';
import {
  CREDIT_DRAFT_KEY,
  arriveWithCard,
  emptyCreditDraft,
  restoreCreditDraft,
  type CreditDraft,
} from '@/features/account-credits/draft';
import { resolveSponsor } from '@/features/account-credits/sponsor';
import {
  HISTORY_STATUS,
  ROW_STATUS,
  blankAmountRows,
  centsToAmountInput,
  creditCsvBlob,
  currentGiftFacts,
  exceedsCheckoutLimit,
  historyStatus,
  isExpired,
  modeForDestination,
  nextRowNumber,
  readCreditCsvFile,
  requestFingerprint,
  resolvedRows,
  rowsNeedingAttention,
  settleCachesAfterPosting,
} from '@/features/account-credits/lib';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Callout } from '@/ui/Callout';
import { DataTable, type Column } from '@/ui/DataTable';
import { ConfirmDialog } from '@/ui/Dialog';
import { Checkbox, Field, Input, Select } from '@/ui/Field';
import { ChipGroup } from '@/ui/Filters';
import { Page, PageBody, PageHeader, Panel, Stat, StatRow } from '@/ui/Panel';

/**
 * Account credits — fund customer accounts from a sponsor's house account, or
 * apply a gift card's unused value to an account.
 *
 * Port of the Svelte page (`server/admin/src/routes/account-credits`), same
 * API and same invariants:
 *
 *   · The editor is browser-local input. "Preview exact credits" hands it to
 *     the server under a fresh request reference, and the server resolves
 *     every identifier, amount and balance into a shared, immutable preview
 *     that any authorized manager can review and post within 30 minutes.
 *   · The reference only changes when the instructions change, so a retry of
 *     the same reviewed draft after a dropped connection posts exactly once.
 *   · Posting is a confirm that names the sponsor and total; identical
 *     instructions posted in the last 24 hours must be acknowledged first.
 *   · Nothing here is optimistic. Every figure after a preview or a posting is
 *     the server's — the committed balances come back in the result.
 */

type Kind = AccountCreditRequest['kind'];
type Mode = 'paid_value' | 'spending_value';

type AccountCreditsSearch = {
  /** Start on the gift tab with this card loaded (from a gift-card page). */
  cardId?: string;
  /** Pre-select this account as the funding sponsor (from a customer page). */
  customerId?: string;
};

export const Route = createFileRoute('/_app/account-credits')({
  staticData: { crumb: 'Account credits' },
  validateSearch: (s: Record<string, unknown>): AccountCreditsSearch => ({
    cardId: asOptionalText(s.cardId),
    customerId: asOptionalText(s.customerId),
  }),
  component: AccountCreditsScreen,
});

const DEFAULT_NOTICE =
  'Editable drafts are saved in this browser; previews and postings are shared with authorized managers.';
const STORAGE_UNAVAILABLE =
  'Browser draft storage is unavailable. Preview your work to save it in shared history.';

function AccountCreditsScreen() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isManager = useAtLeast('manager');

  /* ── Editor state (mirrors CreditDraft) ─────────────────────────────────── */
  const [kind, setKind] = useState<Kind>(search.cardId ? 'gift_refund' : 'batch');
  const [name, setName] = useState('');
  const [sponsor, setSponsor] = useState<CreditAccount | null>(null);
  const [destination, setDestination] = useState<CreditAccount | null>(null);
  const [recipient, setRecipient] = useState<CreditAccount | null>(null);
  const [rows, setRows] = useState<CreditRecipientInput[]>([]);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [cardId, setCardId] = useState(search.cardId ?? '');
  const [mode, setMode] = useState<Mode>('paid_value');
  const [editingRow, setEditingRow] = useState<number | null>(null);
  const [requestId, setRequestId] = useState('');
  const [payload, setPayload] = useState('');

  /* ── Server-held review ─────────────────────────────────────────────────── */
  const [operation, setOperation] = useState<AccountCreditOperation | null>(null);
  const [pendingOperationId, setPendingOperationId] = useState<string | null>(null);
  const [acknowledgeDuplicate, setAcknowledgeDuplicate] = useState(false);
  const [confirming, setConfirming] = useState(false);

  /* ── Notices ────────────────────────────────────────────────────────────── */
  const [problem, setProblem] = useState('');
  const [freshPreview, setFreshPreview] = useState(false);
  const [savedNotice, setSavedNotice] = useState('');
  const [storageReady, setStorageReady] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  const fileInput = useRef<HTMLInputElement>(null);

  const history = useQuery({
    queryKey: ['account-credits', 'history'],
    queryFn: () => api.get<AccountCreditOperation[]>('/account-credits'),
    enabled: isManager,
  });

  const loadedCardId = search.cardId;
  const giftInfo = useQuery({
    queryKey: ['account-credits', 'gift-card', loadedCardId],
    queryFn: () =>
      api.get<AccountCreditGiftInfo>(
        `/account-credits/gift-cards/${encodeURIComponent(loadedCardId ?? '')}`
      ),
    enabled: isManager && !!loadedCardId,
    retry: false,
  });

  const preselectId = search.customerId;
  const preselect = useQuery({
    queryKey: ['account-credits', 'sponsor', preselectId],
    queryFn: () => resolveSponsor(preselectId ?? ''),
    enabled: isManager && !!preselectId,
    staleTime: Infinity,
    retry: false,
  });

  /* ── Draft persistence ──────────────────────────────────────────────────── */

  // The editor the current operation was reviewed for. Recorded whenever an
  // operation is attached (preview, history review, draft restore) and
  // compared against the live editor below: a reviewed operation is only ever
  // posted for the instructions it previewed, no matter which code path
  // changed them.
  const reviewedFor = useRef<string | null>(null);
  const fingerprint = useMemo(
    () =>
      requestFingerprint({ kind, name, sponsor, rows, cardId, mode, destination, note }),
    [kind, name, sponsor, rows, cardId, mode, destination, note]
  );

  function invalidate() {
    reviewedFor.current = null;
    setOperation(null);
    setPendingOperationId(null);
    setRequestId('');
    setPayload('');
    setAcknowledgeDuplicate(false);
    setFreshPreview(false);
  }

  useEffect(() => {
    if (operation && reviewedFor.current !== null && reviewedFor.current !== fingerprint)
      invalidate();
  }, [operation, fingerprint]);

  function applyDraft(saved: CreditDraft) {
    setKind(saved.kind);
    setName(saved.name);
    setSponsor(saved.sponsor);
    setDestination(saved.destination);
    setRecipient(saved.recipient);
    setRows(saved.rows);
    setAmount(saved.amount);
    setNote(saved.note);
    setCardId(saved.cardId);
    setMode(saved.mode);
    setEditingRow(saved.editingRow);
    setRequestId(saved.requestId);
    setPayload(saved.payload);
    setPendingOperationId(saved.operationId);
  }

  // Restore once on mount. Arriving with a card in the URL switches the saved
  // editor to that card rather than discarding it — the batch rows stay under
  // the other tab, and the save effect below never writes an empty gift editor
  // over them.
  const restored = useRef(false);
  useEffect(() => {
    if (restored.current) return;
    restored.current = true;
    try {
      const saved = restoreCreditDraft(localStorage.getItem(CREDIT_DRAFT_KEY));
      if (saved) {
        applyDraft(search.cardId ? arriveWithCard(saved, search.cardId) : saved);
        setSavedNotice('Restored the draft saved in this browser.');
      }
    } catch {
      setSavedNotice(STORAGE_UNAVAILABLE);
    }
    setStorageReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A restored draft names its reviewed operation by id; attach it once the
  // shared history has loaded.
  useEffect(() => {
    if (!pendingOperationId || !history.data) return;
    const recorded = history.data.find(
      (entry) => entry.preview.id === pendingOperationId
    );
    if (recorded) {
      // Every edit drops the operation id from the saved draft, so a draft
      // that still names one is the editor its preview was built from.
      reviewedFor.current = fingerprint;
      setOperation(recorded);
    }
    setPendingOperationId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingOperationId, history.data]);

  // Arriving with a card in the URL while mounted (Load card, or a gift-card
  // page link) switches the editor to that card exactly as typing a new card
  // ID does. A draft already on that card — restored, or the one just typed —
  // is left alone.
  const arrivedFor = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!storageReady || !loadedCardId || arrivedFor.current === loadedCardId) return;
    arrivedFor.current = loadedCardId;
    if (kind === 'gift_refund' && cardId === loadedCardId) return;
    setKind('gift_refund');
    setCardId(loadedCardId);
    setDestination(null);
    setMode('paid_value');
    invalidate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageReady, loadedCardId]);

  // Loading a card fills the destination with its original funding account —
  // but only into a fresh card editor. A restored draft or an operator who
  // already chose a destination, a mode or reviewed a preview keeps those
  // choices; the gift facts arriving late may not overwrite them.
  const giftAppliedFor = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!storageReady || !giftInfo.data || !loadedCardId) return;
    if (giftAppliedFor.current === loadedCardId) return;
    // Not on this card yet (the arrival effect above switches in the same
    // commit): decide on the next render, with the card in place.
    if (kind !== 'gift_refund' || cardId !== loadedCardId) return;
    giftAppliedFor.current = loadedCardId;
    const fresh =
      destination === null &&
      mode === 'paid_value' &&
      !requestId &&
      !operation &&
      !pendingOperationId;
    if (fresh) setDestination(giftInfo.data.destination);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageReady, giftInfo.data, loadedCardId, kind, cardId]);

  // "Allocate credits" from a customer page selects that account as sponsor.
  // The account is read by id; when it cannot be confirmed (missing, inactive,
  // or the read failed) the operator is told to search, never that the
  // account is not a house account — the screen has not established that.
  const preselectAppliedFor = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!storageReady || !preselectId) return;
    if (!preselect.isSuccess && !preselect.isError) return;
    if (preselectAppliedFor.current === preselectId) return;
    preselectAppliedFor.current = preselectId;
    const account = preselect.data ?? null;
    if (!account) {
      setProblem(`Account ${preselectId} could not be confirmed — search for it.`);
      return;
    }
    // Switching tabs or sponsors changes the instructions: a preview reviewed
    // under the gift editor must not stay postable under the batch one.
    if (kind !== 'batch' || sponsor?.id !== account.id) {
      setKind('batch');
      setSponsor(account);
      invalidate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageReady, preselectId, preselect.data, preselect.isSuccess, preselect.isError]);

  useEffect(() => {
    if (!storageReady) return;
    const draft: CreditDraft = {
      kind,
      name,
      sponsor,
      destination,
      recipient,
      rows,
      amount,
      note,
      cardId,
      mode,
      editingRow,
      requestId,
      payload,
      operationId: operation?.preview.id ?? pendingOperationId,
    };
    try {
      localStorage.setItem(CREDIT_DRAFT_KEY, JSON.stringify(draft));
    } catch {
      // The notice reports storage failure without losing editor state.
      setSavedNotice(STORAGE_UNAVAILABLE);
    }
  }, [
    storageReady,
    kind,
    name,
    sponsor,
    destination,
    recipient,
    rows,
    amount,
    note,
    cardId,
    mode,
    editingRow,
    requestId,
    payload,
    operation,
    pendingOperationId,
  ]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  /* ── Mutations ──────────────────────────────────────────────────────────── */

  const preview = useMutation({
    mutationFn: async () => {
      let request: AccountCreditRequest;
      if (kind === 'batch') {
        if (!sponsor) throw new Error('Select the funding house account');
        if (!rows.length) throw new Error('Add at least one recipient');
        request = {
          kind,
          name,
          funding: { kind: 'house_account', customerId: sponsor.id },
          rows,
        };
      } else {
        request = {
          kind,
          cardId: Number(cardId),
          mode,
          ...(destination ? { destinationCustomerId: destination.id } : {}),
          note,
        };
      }
      const next = JSON.stringify(request);
      // The same instructions keep their reference; changed instructions get a
      // new one. A retry after a lost response therefore replays, never re-posts.
      const id = payload !== next || !requestId ? crypto.randomUUID() : requestId;
      setRequestId(id);
      setPayload(next);
      reviewedFor.current = fingerprint;
      return api.post<AccountCreditOperation>('/account-credits/preview', {
        id,
        request,
      });
    },
    onSuccess: (result) => {
      setOperation(result);
      setPendingOperationId(null);
      setAcknowledgeDuplicate(false);
      setFreshPreview(false);
      setProblem('');
      void qc.invalidateQueries({ queryKey: ['account-credits', 'history'] });
    },
    onError: (err) => setProblem(describePreviewFailure(err)),
  });

  const commit = useMutation({
    mutationFn: async (submitted: AccountCreditOperation) => ({
      submitted,
      result: await api.post<AccountCreditResult>(
        `/account-credits/${submitted.preview.id}/commit`,
        { acknowledgeDuplicate }
      ),
    }),
    onSuccess: ({ submitted, result }) => {
      setConfirming(false);
      setFreshPreview(false);
      if (submitted.preview.id === result.id) {
        setOperation({ ...submitted, result, duplicates: [] });
        setProblem('');
      } else {
        setProblem(
          'Posting completed. Open its dated entry in recent history to see the result.'
        );
      }
      // Recipients come from the preview: the result's balances also carry
      // the sponsor's row, which is billed, not credited.
      const billed = submitted.preview.fundingAccount;
      toast.success(
        `Posted ${money(result.totalCents)} to ${countLabel(submitted.preview.recipients)}` +
          (billed ? ` · billed ${billed.name}` : '')
      );
      settleCachesAfterPosting(qc, submitted, result);
    },
    onError: (err) => {
      setConfirming(false);
      const failure = describeCommitFailure(err);
      setProblem(failure.message);
      setFreshPreview(failure.freshPreview);
    },
  });

  const busy = preview.isPending || commit.isPending;

  /* ── Editor actions ─────────────────────────────────────────────────────── */

  function addRecipient() {
    if (!recipient) {
      setProblem('Select an existing recipient account');
      return;
    }
    try {
      const row: CreditRecipientInput = {
        row: editingRow ?? nextRowNumber(rows),
        customerId: recipient.id,
        ...(recipient.accountNumber ? { accountNumber: recipient.accountNumber } : {}),
        recipientName: recipient.name,
        amountCents: parseCreditAmount(amount),
        note,
      };
      setRows(
        editingRow === null
          ? [...rows, row]
          : rows.map((r) => (r.row === editingRow ? row : r))
      );
      invalidate();
      setEditingRow(null);
      setRecipient(null);
      setAmount('');
      setNote('');
      setProblem('');
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : 'Invalid amount');
    }
  }

  function editRow(row: CreditRecipientInput) {
    setEditingRow(row.row);
    setAmount(centsToAmountInput(row.amountCents));
    setNote(row.note ?? '');
    setRecipient(
      operation?.preview.rowStatuses.find((status) => status.row === row.row)?.account ??
        null
    );
    invalidate();
  }

  function removeRow(row: CreditRecipientInput) {
    setRows(rows.filter((r) => r.row !== row.row));
    if (editingRow === row.row) setEditingRow(null);
    invalidate();
  }

  async function importFile(file: File | undefined) {
    if (!file) return;
    try {
      setRows(await readCreditCsvFile(file));
      setEditingRow(null);
      invalidate();
      setProblem('');
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : 'Invalid CSV');
    }
    if (fileInput.current) fileInput.current.value = '';
  }

  function download(values: CreditRecipientInput[], filename: string) {
    try {
      saveBlob(creditCsvBlob(values), filename);
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : 'Export failed');
    }
  }

  function restoreOperation(entry: AccountCreditOperation) {
    // The editor the entry is restored into, set as one unit so the
    // fingerprint it is reviewed for is the fingerprint the next render sees.
    const restoredDestination =
      entry.request.kind === 'gift_refund' && entry.request.destinationCustomerId
        ? (entry.preview.recipients[0]?.account ?? null)
        : null;
    const editor =
      entry.request.kind === 'batch'
        ? {
            kind: entry.request.kind,
            name: entry.request.name,
            sponsor: entry.preview.fundingAccount,
            rows: structuredClone(entry.request.rows),
            cardId,
            mode,
            destination,
            note: '',
          }
        : {
            kind: entry.request.kind,
            name,
            sponsor,
            rows,
            cardId: String(entry.request.cardId),
            mode: entry.request.mode,
            destination: restoredDestination,
            note: entry.request.note,
          };
    reviewedFor.current = requestFingerprint(editor);
    setOperation(entry);
    setPendingOperationId(null);
    setRequestId(entry.preview.id);
    setPayload(JSON.stringify(entry.request));
    setAcknowledgeDuplicate(false);
    setFreshPreview(false);
    setKind(editor.kind);
    setRecipient(null);
    setAmount('');
    setNote(editor.note);
    setEditingRow(null);
    if (entry.request.kind === 'batch') {
      setName(editor.name);
      setSponsor(editor.sponsor);
      setRows(editor.rows);
    } else {
      setCardId(editor.cardId);
      setMode(editor.mode);
      setDestination(editor.destination);
    }
    setProblem('');
    setSavedNotice('');
  }

  const [discarding, setDiscarding] = useState(false);
  // Discard clears the editable input but keeps the tab, card and mode the
  // operator is on (the Svelte page's rule), so a `?cardId=` arrival still
  // shows its card afterwards.
  function discardDraft() {
    applyDraft({ ...emptyCreditDraft(), kind, cardId, mode });
    invalidate();
    setSavedNotice('');
    setProblem('');
    setDiscarding(false);
  }

  function startFresh() {
    invalidate();
    setProblem('');
  }

  function loadCard() {
    const id = cardId.trim();
    if (!/^[1-9]\d*$/.test(id)) {
      setProblem('Enter the gift card ID as a whole number.');
      return;
    }
    setProblem('');
    void navigate({ to: '/account-credits', search: { cardId: id } });
  }

  /* ── Derived ────────────────────────────────────────────────────────────── */

  const expired = operation ? isExpired(operation, now) : false;
  const statusByRow = useMemo(
    () => new Map(operation?.preview.rowStatuses.map((s) => [s.row, s]) ?? []),
    [operation]
  );
  const attention = operation ? rowsNeedingAttention(operation.preview) : 0;
  const loadedGift =
    loadedCardId && cardId === loadedCardId && giftInfo.data
      ? giftInfo.data.gift
      : undefined;
  const giftFacts = currentGiftFacts(
    cardId,
    operation?.preview.gift,
    loadedCardId,
    loadedGift
  );

  const rowColumns: Array<Column<CreditRecipientInput>> = [
    { id: 'row', header: 'Source row', width: '6rem', mono: true, cell: (r) => r.row },
    {
      id: 'name',
      header: 'Recipient / imported name',
      cell: (r) => r.recipientName || '—',
    },
    {
      id: 'customerId',
      header: 'Customer ID',
      width: '9rem',
      mono: true,
      cell: (r) => r.customerId ?? <span className="text-ink-faint">by account no.</span>,
    },
    {
      id: 'accountNumber',
      header: 'Account no.',
      width: '8rem',
      mono: true,
      cell: (r) => r.accountNumber ?? '—',
    },
    {
      id: 'amount',
      header: 'Amount',
      width: '7rem',
      align: 'right',
      mono: true,
      cell: (r) => money(r.amountCents),
    },
    {
      id: 'status',
      header: 'Validation',
      cell: (r) => {
        const status = statusByRow.get(r.row);
        if (!status) return <Badge tone="neutral">Not yet checked</Badge>;
        return (
          <span className="flex min-w-0 items-center gap-2">
            <Badge tone={ROW_STATUS[status.status].tone}>
              {ROW_STATUS[status.status].label}
            </Badge>
            {status.status === 'matched' ? (
              <span className="text-2xs text-ink-faint truncate">
                {status.account?.name}
                {status.account?.accountNumber
                  ? ` · #${status.account.accountNumber}`
                  : ''}
              </span>
            ) : (
              <span className="text-2xs text-ink-muted truncate" title={status.message}>
                {status.message}
              </span>
            )}
          </span>
        );
      },
    },
    {
      id: 'actions',
      header: '',
      width: '11rem',
      align: 'right',
      cell: (r) => (
        <span className="flex justify-end gap-1">
          <Button size="xs" variant="ghost" onClick={() => editRow(r)}>
            Resolve / edit
          </Button>
          <Button size="xs" variant="ghost" onClick={() => removeRow(r)}>
            Remove
          </Button>
        </span>
      ),
    },
  ];

  const historyColumns: Array<Column<AccountCreditOperation>> = [
    {
      id: 'created',
      header: 'Created',
      width: '11rem',
      mono: true,
      cell: (e) => dateTime(e.createdAt),
    },
    { id: 'name', header: 'Batch', cell: (e) => e.preview.name },
    {
      id: 'total',
      header: 'Total',
      width: '7rem',
      align: 'right',
      mono: true,
      cell: (e) => money(e.preview.totalCents),
    },
    {
      id: 'recipients',
      header: 'Recipients',
      width: '6rem',
      align: 'right',
      mono: true,
      cell: (e) => num(e.preview.recipients.length),
    },
    {
      id: 'status',
      header: 'Status',
      width: '9rem',
      cell: (e) => {
        const status = HISTORY_STATUS[historyStatus(e, now)];
        return (
          <Badge tone={status.tone} dot>
            {status.label}
          </Badge>
        );
      },
    },
    {
      id: 'review',
      header: '',
      width: '6rem',
      align: 'right',
      cell: (e) => (
        <Button
          size="xs"
          variant={operation?.preview.id === e.preview.id ? 'primary' : 'default'}
          onClick={() => restoreOperation(e)}
        >
          Review
        </Button>
      ),
    },
  ];

  /* ── Render ─────────────────────────────────────────────────────────────── */

  return (
    <Page>
      <PageHeader
        eyebrow="People"
        title="Account credits"
        subtitle="Fund customer accounts or apply unused gift-card value. Credits pay down debt first; any remainder stays available for purchases."
        actions={
          <>
            <Button asChild variant="ghost" size="sm">
              <Link
                to="/customers"
                search={{ q: '', sort: 'balance', order: 'desc', page: 1 }}
              >
                Customers
              </Link>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              icon={<RotateCcw className="size-3.5" />}
              disabled={busy}
              onClick={() => setDiscarding(true)}
            >
              Discard draft
            </Button>
          </>
        }
      />
      <PageBody>
        <div className="mx-auto flex max-w-6xl flex-col gap-3">
          {!isManager ? (
            <Callout
              tone="warn"
              title="Manager sign-in required"
              icon={<AlertTriangle />}
            >
              Only managers and admins can preview and post account credits. From this
              sign-in the editor below is read-only; recent drafts and postings can still
              be looked at.
            </Callout>
          ) : null}

          {problem ? (
            <Callout tone="negative" icon={<AlertTriangle />} title="Needs attention">
              <p role="alert" className="whitespace-pre-line">
                {problem}
              </p>
              {freshPreview ? (
                <Button size="xs" className="mt-2" onClick={startFresh}>
                  Start fresh preview
                </Button>
              ) : null}
            </Callout>
          ) : null}

          <p className="text-2xs text-ink-faint">{savedNotice || DEFAULT_NOTICE}</p>

          <fieldset disabled={busy || !isManager} className="contents">
            <Panel
              title="Draft"
              description="Editable input. Nothing posts until a preview is reviewed and confirmed."
              actions={
                <ChipGroup<Kind>
                  value={kind}
                  onChange={(next) => {
                    setKind(next);
                    invalidate();
                  }}
                  options={[
                    { value: 'batch', label: 'Bulk account credits' },
                    { value: 'gift_refund', label: 'Apply gift-card value' },
                  ]}
                />
              }
            >
              {kind === 'batch' ? (
                <div className="flex flex-col gap-3">
                  <div className="grid gap-3 md:grid-cols-2">
                    <Field label="Batch name" required htmlFor="credit-batch-name">
                      <Input
                        id="credit-batch-name"
                        value={name}
                        placeholder="Holiday bonuses"
                        maxLength={255}
                        onChange={(e) => {
                          setName(e.currentTarget.value);
                          invalidate();
                        }}
                      />
                    </Field>
                    <AccountPicker
                      label="Funding house account"
                      hint="Receives one bill for the full total. Each recipient receives the amount entered below."
                      selected={sponsor}
                      onSelect={(account) => {
                        setSponsor(account);
                        invalidate();
                      }}
                      onClear={() => {
                        setSponsor(null);
                        invalidate();
                      }}
                    />
                  </div>

                  <div className="border-line-soft border-t pt-3">
                    <h3 className="text-2xs text-ink-faint mb-2 font-medium tracking-wider uppercase">
                      Recipients
                    </h3>
                    <div className="grid gap-3 md:grid-cols-[2fr_1fr_1fr_auto] md:items-end">
                      <AccountPicker
                        label={
                          editingRow === null
                            ? 'Add recipient'
                            : `Recipient on source row ${editingRow}`
                        }
                        selected={recipient}
                        onSelect={setRecipient}
                        onClear={() => setRecipient(null)}
                      />
                      <Field label="Amount" htmlFor="credit-amount">
                        <Input
                          id="credit-amount"
                          mono
                          inputMode="decimal"
                          value={amount}
                          prefix={<span className="font-mono text-xs">$</span>}
                          className="text-right"
                          onChange={(e) => setAmount(e.currentTarget.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addRecipient();
                            }
                          }}
                        />
                      </Field>
                      <Field label="Note" htmlFor="credit-note">
                        <Input
                          id="credit-note"
                          value={note}
                          maxLength={1000}
                          onChange={(e) => setNote(e.currentTarget.value)}
                        />
                      </Field>
                      <Button onClick={addRecipient}>
                        {editingRow === null ? 'Add recipient' : 'Update row'}
                      </Button>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-1.5">
                    <input
                      ref={fileInput}
                      type="file"
                      accept=".csv,text/csv"
                      className="sr-only"
                      aria-label="Import CSV"
                      onChange={(e) => void importFile(e.currentTarget.files?.[0])}
                    />
                    <Button
                      size="sm"
                      icon={<Upload className="size-3.5" />}
                      onClick={() => fileInput.current?.click()}
                    >
                      Import CSV
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      icon={<Download className="size-3.5" />}
                      onClick={() => download([], 'account-credit-template.csv')}
                    >
                      Blank CSV
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      icon={<Download className="size-3.5" />}
                      disabled={!rows.length}
                      onClick={() =>
                        download(blankAmountRows(rows), 'selected-accounts.csv')
                      }
                    >
                      Export selected accounts
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      icon={<Download className="size-3.5" />}
                      disabled={!rows.length}
                      onClick={() => download(rows, 'account-credit-draft.csv')}
                    >
                      Export draft
                    </Button>
                    <span className="text-2xs text-ink-faint ml-auto">
                      Up to 500 rows / 256 KiB. Use customer_id or exact account_number;
                      keep leading zeros as text. Imported names are display context only.
                    </span>
                  </div>

                  {rows.length ? (
                    <div className="border-line -mx-3 -mb-3 border-t">
                      {operation && attention > 0 ? (
                        <div className="border-line bg-warn-muted text-ink border-b px-3 py-1.5 text-xs">
                          {num(attention)} {attention === 1 ? 'row needs' : 'rows need'}{' '}
                          attention. Resolve or remove each flagged row, then preview
                          again.
                        </div>
                      ) : null}
                      <DataTable
                        rows={rows}
                        columns={rowColumns}
                        rowKey={(r) => r.row}
                        keyboard={false}
                        rowTone={(r) => {
                          const status = statusByRow.get(r.row);
                          if (!status || status.status === 'matched') return null;
                          return status.status === 'unmatched' ? 'warn' : 'negative';
                        }}
                        footer={
                          <span className="font-mono tabular-nums">
                            {num(rows.length)} {rows.length === 1 ? 'row' : 'rows'} ·{' '}
                            {money(rows.reduce((sum, r) => sum + r.amountCents, 0))}
                          </span>
                        }
                      />
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  <div className="grid gap-3 md:grid-cols-2">
                    <Field
                      label="Gift card ID"
                      htmlFor="credit-card-id"
                      hint={
                        <>
                          Open a card from{' '}
                          <Link
                            to="/gift-cards"
                            search={{
                              q: '',
                              status: 'all',
                              sort: 'created',
                              order: 'desc',
                              page: 1,
                            }}
                            className="text-signal-ink underline-offset-2 hover:underline"
                          >
                            Gift cards
                          </Link>{' '}
                          to start with its ID. Its original funding record is preserved.
                        </>
                      }
                      error={
                        giftInfo.isError && cardId === loadedCardId
                          ? giftMessage(giftInfo.error)
                          : null
                      }
                    >
                      <div className="flex items-center gap-1.5">
                        <Input
                          id="credit-card-id"
                          mono
                          inputMode="numeric"
                          value={cardId}
                          onChange={(e) => {
                            setCardId(e.currentTarget.value);
                            setDestination(null);
                            setMode('paid_value');
                            invalidate();
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              loadCard();
                            }
                          }}
                        />
                        <Button
                          size="md"
                          loading={giftInfo.isFetching}
                          onClick={loadCard}
                        >
                          Load card
                        </Button>
                      </div>
                    </Field>
                    {loadedGift ? (
                      <Callout tone="info" title={`Card ${loadedGift.code}`}>
                        Unused available face {money(loadedGift.availableFaceCents)} ·
                        initial face {money(loadedGift.faceCents)} · purchase-price basis{' '}
                        {loadedGift.paidCents === null
                          ? 'unknown'
                          : money(loadedGift.paidCents)}{' '}
                        · original funding account{' '}
                        {giftInfo.data?.destination
                          ? `${giftInfo.data.destination.name} (ID ${giftInfo.data.destination.id})`
                          : (loadedGift.originalFundingCustomerId ?? 'unknown')}
                        .
                      </Callout>
                    ) : null}
                  </div>

                  <AccountPicker
                    label="Destination account"
                    hint="Paid refunds default to the original purchaser; spending transfers require an explicit selection."
                    selected={destination}
                    onSelect={(account) => {
                      setDestination(account);
                      setMode(modeForDestination(giftFacts, account));
                      invalidate();
                    }}
                  />
                  <div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setDestination(null);
                        setMode('paid_value');
                        invalidate();
                      }}
                    >
                      Use original funding account
                    </Button>
                  </div>

                  <div className="grid gap-3 md:grid-cols-2">
                    <Field
                      label="How to apply the unused value"
                      htmlFor="credit-gift-mode"
                      hint="Refund paid value normally returns funds to the purchaser. Transfer spending value moves the intended recipient's shopping value to their account. Choose explicitly when they differ."
                    >
                      <Select
                        id="credit-gift-mode"
                        value={mode}
                        onChange={(e) => {
                          setMode(e.currentTarget.value as Mode);
                          invalidate();
                        }}
                      >
                        <option value="paid_value">
                          Refund paid value — proportional to the amount paid
                        </option>
                        <option value="spending_value">
                          Transfer spending value — full unused face value
                        </option>
                      </Select>
                    </Field>
                    <Field label="Reason" required htmlFor="credit-gift-reason">
                      <Input
                        id="credit-gift-reason"
                        value={note}
                        maxLength={1000}
                        onChange={(e) => {
                          setNote(e.currentTarget.value);
                          invalidate();
                        }}
                      />
                    </Field>
                  </div>
                </div>
              )}

              <div className="border-line-soft mt-3 flex items-center justify-end gap-2 border-t pt-3">
                <Button
                  variant="primary"
                  loading={preview.isPending}
                  icon={<HandCoins className="size-3.5" />}
                  onClick={() => preview.mutate()}
                >
                  Preview exact credits
                </Button>
              </div>
            </Panel>

            {operation ? (
              <ReviewPanel
                operation={operation}
                expired={expired}
                attention={attention}
                acknowledgeDuplicate={acknowledgeDuplicate}
                onAcknowledge={setAcknowledgeDuplicate}
                onExport={() =>
                  download(resolvedRows(operation), 'resolved-account-credits.csv')
                }
                onStartFresh={startFresh}
                onPost={() => setConfirming(true)}
                posting={commit.isPending}
              />
            ) : null}

            <Panel
              title="Recent drafts and postings"
              description="Shared with every manager. After a timeout, reopen the same draft and retry — the same reference posts only once."
              bleed
            >
              <DataTable
                rows={history.data}
                columns={historyColumns}
                rowKey={(e) => e.preview.id}
                loading={history.isPending && isManager}
                error={history.error}
                onRetry={() => void history.refetch()}
                keyboard={false}
                isRowActive={(e) => operation?.preview.id === e.preview.id}
                empty={{ title: 'No account credit batches yet' }}
              />
            </Panel>
          </fieldset>
        </div>
      </PageBody>

      {operation ? (
        <ConfirmDialog
          open={confirming}
          onOpenChange={setConfirming}
          title="Post these credits?"
          description="This records permanent financial entries. There is no undo."
          confirmLabel="Post credits"
          variant="primary"
          // Same threshold as a gift-card batch: a four-figure posting earns the
          // typed word, a small one keeps the confirm light.
          typeToConfirm={operation.preview.totalCents >= 100_000 ? 'POST' : undefined}
          loading={commit.isPending}
          consequences={consequences(operation)}
          onConfirm={() => commit.mutate(operation)}
        />
      ) : null}

      <ConfirmDialog
        open={discarding}
        onOpenChange={setDiscarding}
        title="Discard the editable draft?"
        description="Clears the draft saved in this browser. Posted credits and reviewed history remain on file."
        confirmLabel="Discard draft"
        onConfirm={discardDraft}
      />
    </Page>
  );
}

/* ── Review panel ─────────────────────────────────────────────────────────── */

function ReviewPanel({
  operation,
  expired,
  attention,
  acknowledgeDuplicate,
  onAcknowledge,
  onExport,
  onStartFresh,
  onPost,
  posting,
}: {
  operation: AccountCreditOperation;
  expired: boolean;
  attention: number;
  acknowledgeDuplicate: boolean;
  onAcknowledge: (next: boolean) => void;
  onExport: () => void;
  onStartFresh: () => void;
  onPost: () => void;
  posting: boolean;
}) {
  const { preview, result } = operation;
  const funding = preview.fundingAccount;
  const gift = preview.gift;
  const canPost =
    !result &&
    preview.postable &&
    !expired &&
    (operation.duplicates.length === 0 || acknowledgeDuplicate);

  const recipientColumns: Array<
    Column<AccountCreditOperation['preview']['recipients'][number]>
  > = [
    { id: 'row', header: 'Row', width: '4rem', mono: true, cell: (r) => r.row },
    {
      id: 'account',
      header: 'Matched account',
      cell: (r) => (
        <span className="flex min-w-0 flex-col">
          <span className="truncate">{r.account.name}</span>
          <span className="text-2xs text-ink-faint truncate font-mono tabular-nums">
            ID {r.account.id} ·{' '}
            {r.account.accountNumber
              ? `#${r.account.accountNumber}`
              : 'No account number'}
          </span>
        </span>
      ),
    },
    { id: 'imported', header: 'Imported name', cell: (r) => r.importedName ?? '—' },
    {
      id: 'credit',
      header: 'Credit',
      width: '7rem',
      align: 'right',
      mono: true,
      cell: (r) => money(r.amountCents),
    },
    {
      id: 'balance',
      header: 'Balance at preview → after credit',
      width: '14rem',
      align: 'right',
      mono: true,
      cell: (r) =>
        `${money(r.account.balanceCents)} → ${money(r.account.balanceCents - r.amountCents)}`,
    },
  ];

  const status: ReactNode = result
    ? `Posted ${dateTime(result.completedAt)}`
    : expired
      ? 'Expired — edit and create a fresh preview'
      : `Preview valid until ${time(operation.expiresAt)}`;

  return (
    <Panel
      title={preview.name}
      description={`Created ${dateTime(operation.createdAt)} · ${status}`}
      actions={
        <>
          {operation.request.kind === 'batch' ? (
            <Button
              size="sm"
              variant="ghost"
              icon={<Download className="size-3.5" />}
              onClick={onExport}
            >
              Export matched recipients
            </Button>
          ) : null}
          {expired && !result ? (
            <Button size="sm" onClick={onStartFresh}>
              Start fresh preview
            </Button>
          ) : null}
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {!preview.postable ? (
          <Callout
            tone="negative"
            icon={<AlertTriangle />}
            title={`${num(attention)} ${attention === 1 ? 'row needs' : 'rows need'} attention`}
          >
            <p role="alert">
              {[...preview.errors, 'Correct the draft and preview again.'].join(' ')}
            </p>
          </Callout>
        ) : null}

        <StatRow>
          <Stat label="Total credit" value={money(preview.totalCents)} />
          <Stat label="Recipients" value={num(preview.recipients.length)} />
          {funding ? (
            <Stat
              label="Sponsor bill"
              value={money(preview.totalCents)}
              sub={`${funding.name} · ${money(funding.balanceCents)} → ${money(funding.balanceCents + preview.totalCents)}`}
              tone={
                exceedsCheckoutLimit(funding, preview.totalCents) ? 'warn' : undefined
              }
            />
          ) : null}
          {gift ? (
            <Stat
              label="Face consumed"
              value={money(gift.availableFaceCents)}
              sub={`card ${gift.code} · ${money(gift.heldCents)} stays held`}
            />
          ) : null}
        </StatRow>

        {funding ? (
          <Callout
            tone={exceedsCheckoutLimit(funding, preview.totalCents) ? 'warn' : 'info'}
            title="Manager billing authorization"
          >
            Charge {funding.name} (ID {funding.id}) {money(preview.totalCents)}. Balance
            at preview {money(funding.balanceCents)} →{' '}
            {money(funding.balanceCents + preview.totalCents)}. House credit is{' '}
            {funding.hasCredit ? 'enabled' : 'not enabled'}; configured limit{' '}
            {funding.hasCredit && funding.creditLimitCents <= 0
              ? 'none (unlimited)'
              : money(funding.creditLimitCents)}
            . This administrative charge may exceed that limit.
            {exceedsCheckoutLimit(funding, preview.totalCents) ? (
              <strong className="text-ink block">
                The resulting debt exceeds the account’s normal checkout limit.
              </strong>
            ) : null}
          </Callout>
        ) : null}

        {gift ? (
          <Callout tone="info" title={`Card ${gift.code}`}>
            Consume {money(gift.availableFaceCents)} face value; {money(gift.heldCents)}{' '}
            remains held for checkout. Initial face {money(gift.faceCents)}. Original
            purchase-price basis{' '}
            {gift.paidCents === null ? 'unknown' : money(gift.paidCents)}. Original
            funding account {gift.originalFundingCustomerId ?? 'unknown'}.
          </Callout>
        ) : null}

        <div className="border-line -mx-3 border-y">
          <DataTable
            rows={preview.recipients}
            columns={recipientColumns}
            rowKey={(r) => r.row}
            keyboard={false}
            empty={{ title: 'No matched recipients yet' }}
          />
        </div>

        {result ? (
          <>
            <ul
              aria-label="Posted account balances"
              className="flex flex-col gap-1 text-xs"
            >
              {result.balances.map((balance) => (
                <li key={balance.customerId} className="flex items-baseline gap-2">
                  <span className="min-w-0 flex-1 truncate">{balance.name}</span>
                  <span className="text-ink-muted font-mono tabular-nums">
                    posted {money(balance.deltaCents)}
                  </span>
                  <span className="font-mono tabular-nums">
                    balance after posting {money(balance.balanceCents)}
                  </span>
                </li>
              ))}
            </ul>
            <Callout tone="positive" title="Posted">
              <p role="status">
                Posted {money(result.totalCents)} on {dateTime(result.completedAt)}.{' '}
                <Link
                  to="/customers"
                  search={{ q: '', sort: 'balance', order: 'desc', page: 1 }}
                  className="text-signal-ink underline-offset-2 hover:underline"
                >
                  View account ledgers
                </Link>
              </p>
            </Callout>
          </>
        ) : (
          <>
            <p className="text-2xs text-ink-faint">
              Preview expires after 30 minutes. Live account activity may change these
              balances before posting. This action records permanent financial entries and
              has no undo.
            </p>
            {operation.duplicates.length ? (
              <Callout tone="negative" icon={<AlertTriangle />} title="Posted before">
                <p>
                  A batch with these exact instructions was posted in the last 24 hours:
                </p>
                <ul className="my-1.5 list-disc pl-4 font-mono tabular-nums">
                  {operation.duplicates.map((duplicate) => (
                    <li key={duplicate.id}>
                      {duplicate.name} · {money(duplicate.totalCents)} ·{' '}
                      {dateTime(duplicate.completedAt)}
                    </li>
                  ))}
                </ul>
                <Checkbox
                  checked={acknowledgeDuplicate}
                  onCheckedChange={onAcknowledge}
                  label="I reviewed the earlier posting and intend to post this batch again."
                />
              </Callout>
            ) : null}
            <div className="flex justify-end">
              <Button
                variant="primary"
                disabled={!canPost}
                loading={posting}
                onClick={onPost}
              >
                Confirm and post credits
              </Button>
            </div>
          </>
        )}
      </div>
    </Panel>
  );
}

/* ── Helpers ──────────────────────────────────────────────────────────────── */

function consequences(operation: AccountCreditOperation): string[] {
  const { preview } = operation;
  const items = [
    `Credits ${money(preview.totalCents)} to ${countLabel(preview.recipients)}`,
  ];
  if (preview.fundingAccount) {
    const funding = preview.fundingAccount;
    items.push(
      `Bills ${funding.name} (ID ${funding.id}) ${money(preview.totalCents)} — balance ${money(funding.balanceCents)} → ${money(funding.balanceCents + preview.totalCents)}`
    );
    if (exceedsCheckoutLimit(funding, preview.totalCents))
      items.push('The resulting debt exceeds this account’s normal checkout limit');
  }
  if (preview.gift) {
    items.push(
      `Consumes ${money(preview.gift.availableFaceCents)} face value from card ${preview.gift.code}`
    );
    const destination = preview.recipients[0]?.account;
    if (destination) items.push(`Credits ${destination.name} (ID ${destination.id})`);
  }
  if (operation.duplicates.length)
    items.push('Repeats instructions already posted in the last 24 hours (acknowledged)');
  items.push('Permanent ledger entries — no undo');
  return items;
}

function countLabel(list: readonly unknown[]): string {
  return `${num(list.length)} ${list.length === 1 ? 'account' : 'accounts'}`;
}

function giftMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Gift card unavailable';
}
